import { APP_INITIALIZER, ErrorHandler, LOCALE_ID, NgModule } from '@angular/core';
import { BrowserModule } from '@angular/platform-browser';

import { HTTP_INTERCEPTORS, HttpClient, HttpClientModule } from '@angular/common/http';
import { BrowserAnimationsModule } from '@angular/platform-browser/animations';
import { DOCUMENT, Location } from '@angular/common';
import { Router } from '@angular/router';
import { ClarityModule } from '@clr/angular';
import { AppRoutingModule } from './app-routing.module';
import { AppComponent } from './app.component';
import { initializer } from './utils/app-init';

import { registerLocaleData } from '@angular/common';
import { CdsModule } from '@cds/angular';
import { TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';
import { ToastrModule } from 'ngx-toastr';
import { GlobalErrorHandler } from './global-error-handler';
import localeFrBeExtra from '@angular/common/locales/extra/fr-BE';
import localeFrBe from '@angular/common/locales/fr-BE';
import { SharedModule } from './shared/shared.module';
import { AppConfig } from './shared/utils/config/app-config';
import { ThemeDisplayService } from './services/theme-display.service';
import { ThemeProcessorService } from './services/theme-processor.service';
import { ApplicationTopMenuComponent } from '@app/components/common/menu/top/application-top-menu.component';
import { ApplicationLeftMenuComponent } from '@app/components/common/menu/left/application-left-menu.component';
import { ServicesHealthCheckComponent } from '@app/components/common/services-health-check/services-health-check.component';
import {
	MSAL_GUARD_CONFIG,
	MSAL_INSTANCE,
	MSAL_INTERCEPTOR_CONFIG,
	MsalBroadcastService,
	MsalGuard,
	MsalGuardConfiguration,
	MsalInterceptor,
	MsalInterceptorConfiguration,
	MsalService
} from '@azure/msal-angular';
import { IPublicClientApplication } from '@azure/msal-browser';
import { MSALGuardConfigFactory, MSALInstanceFactory, MSALInterceptorConfigFactory } from './services/authentication/auth.service';

registerLocaleData(localeFrBe, 'fr-BE', localeFrBeExtra);

export function HttpLoaderFactory(httpClient: HttpClient) {
	return new TranslateHttpLoader(httpClient);
}

@NgModule({
	declarations: [AppComponent],
	imports: [
		BrowserModule,
		AppRoutingModule,
		HttpClientModule,
		ClarityModule,
		BrowserAnimationsModule,
		TranslateModule,
		SharedModule,
		TranslateModule.forRoot({
			loader: {
				provide: TranslateLoader,
				useFactory: HttpLoaderFactory,
				deps: [HttpClient]
			}
		}),
		ToastrModule.forRoot({
			timeOut: 4000,
			positionClass: 'toast-bottom-right'
		}),
		CdsModule,
		ApplicationTopMenuComponent,
		ApplicationLeftMenuComponent,
		ServicesHealthCheckComponent
	],
	providers: [
		ThemeProcessorService,
		{
			provide: ErrorHandler,
			useClass: GlobalErrorHandler
		},
		ThemeDisplayService,
		{ provide: LOCALE_ID, useValue: 'fr-BE' },
		{
			provide: APP_INITIALIZER,
			useFactory: initializer,
			multi: true,
			deps: [AppConfig]
		},
		{
			provide: MSAL_INSTANCE,
			useFactory: MSALInstanceFactory
		},
		{
			provide: MSAL_GUARD_CONFIG,
			useFactory: MSALGuardConfigFactory
		},
		{
			provide: MSAL_INTERCEPTOR_CONFIG,
			useFactory: MSALInterceptorConfigFactory
		},
		// NOTE: @azure/msal-angular@2.5.13 ships View-Engine-style metadata (`ctorParameters` +
		// manual `decorators`), not Ivy `ɵfac`. Providing these classes "bare" (or via plain
		// `useClass`) makes Angular's Ivy JIT compiler compile a `ɵfac` for them on the fly the
		// first time they're requested — which reliably threw NG0204 "Circular dependency in DI
		// detected" here (root-caused via bisection across app.module.ts's `providers` array +
		// reading the actual ctorParameters metadata in
		// node_modules/@azure/msal-angular/fesm2015/azure-msal-angular.js). Workaround: provide
		// each one via an explicit `useFactory` + `deps` so Angular never needs to JIT-compile
		// their own `ɵfac` — only the (Ivy-native, already-compiled) `deps` tokens are resolved.
		{
			provide: MsalService,
			useFactory: (instance: IPublicClientApplication, location: Location) => new MsalService(instance, location),
			deps: [MSAL_INSTANCE, Location]
		},
		{
			provide: MsalBroadcastService,
			useFactory: (instance: IPublicClientApplication, authService: MsalService) =>
				new MsalBroadcastService(instance, authService),
			deps: [MSAL_INSTANCE, MsalService]
		},
		{
			provide: MsalGuard,
			useFactory: (
				guardConfig: MsalGuardConfiguration,
				broadcastService: MsalBroadcastService,
				authService: MsalService,
				location: Location,
				router: Router
			) => new MsalGuard(guardConfig, broadcastService, authService, location, router),
			deps: [MSAL_GUARD_CONFIG, MsalBroadcastService, MsalService, Location, Router]
		},
		{
			provide: HTTP_INTERCEPTORS,
			// NOTE (2026-09-28): MsalInterceptor's REAL constructor takes 5 params — the 5th being
			// `@Inject(DOCUMENT) document` (confirmed by reading `MsalInterceptor.ctorParameters` in
			// node_modules/@azure/msal-angular/fesm2015/azure-msal-angular.js directly). It was
			// missing here since 2026-09-25's NG0204 fix — harmless as long as `protectedResourceMap`
			// stayed empty (interceptor short-circuits before ever touching `_document` when there
			// are no scopes to resolve), but as soon as the map got populated (this week's fix), EVERY
			// single HTTP call in the app started throwing `TypeError: this._document is undefined`
			// synchronously inside `getAbsoluteUrl()` — silently breaking i18n/bundles/sales-context/
			// literally everything, with no request ever reaching the network. See TODO-NEXT.md item
			// 14 for the full writeup.
			useFactory: (
				config: MsalInterceptorConfiguration,
				authService: MsalService,
				location: Location,
				broadcastService: MsalBroadcastService,
				document: Document
			) => new MsalInterceptor(config, authService, location, broadcastService, document),
			deps: [MSAL_INTERCEPTOR_CONFIG, MsalService, Location, MsalBroadcastService, DOCUMENT],
			multi: true
		}
	],
	exports: [],
	bootstrap: [AppComponent]
})
export class AppModule {}
